import React, { useState } from "react";
import {
  Container,
  Form,
  Button,
  Card,
  Spinner,
  Row,
  Col,
  Modal,
} from "react-bootstrap";
import { HelpModal } from "../../common/compoents/HelpModal";
import { getSeoGradedContent } from "../../../services/getAiResponse";
import { useToolsStore, type ToolsState } from "../../../store/useToolsStore";
import { IoMdPlay } from "react-icons/io";
import { ToolsHeader } from "../../components/ToolsHeader";
import { isInIframe } from "../../../utils/is-in-iframe";

export const SeoGradedContent: React.FC = () => {
  const {
    content,
    aiPercentage,
    seoGrade,
    optimizedContent,
    isLoading,
    minLengthError,
    setContent,
    setAiPercentage,
    setSeoGrade,
    setOptimizedContent,
    setIsLoading,
    setMinLengthError,
  } = useToolsStore<ToolsState>((state) => state);

  const [showVideoModal, setShowVideoModal] = useState<boolean>(false);

  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newContent = e.target.value;
    setContent(newContent);
    setMinLengthError(null);
  };

  const openVideoModal = () => {
    setShowVideoModal(true);
  };

  const closeVideoModal = () => {
    setShowVideoModal(false);
  };

  const analyzeContent = async () => {
    if (content.length < 100) {
      setMinLengthError("You need to enter at least 100 characters.");
      return;
    }

    setMinLengthError(null);
    setIsLoading(true);
    try {
      const response = await getSeoGradedContent(content);
      if (response) {
        const data: any = JSON.parse(
          response.replaceAll("```json", "").replaceAll("```", "")
        );
      }
    } catch (error) {
      console.error("Error analyzing content:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const renderDonutChart = (percentage: number) => {
    const goodPercentage = percentage;
    const remainingPercentage = 100 - percentage;

    return (
      <svg width="100" height="100" viewBox="0 0 36 36" className="donut-chart">
        <circle
          cx="18"
          cy="18"
          r="15.9"
          fill="transparent"
          stroke="#CCDFF3"
          strokeWidth="3.2"
          strokeDasharray="100 0"
        />
        <circle
          cx="18"
          cy="18"
          r="15.9"
          fill="transparent"
          stroke="#00C500"
          strokeWidth="3.2"
          strokeDasharray={`${goodPercentage} ${remainingPercentage}`}
          strokeDashoffset="25"
        />
        <text
          x="18"
          y="18"
          textAnchor="middle"
          alignmentBaseline="middle"
          fontSize="7"
          fontWeight="bold"
        >
          {`${percentage}%`}
        </text>
      </svg>
    );
  };

  const helpSteps = [
    "Paste the content you want to analyze into the designated text area.",
    "Click 'Analyze Content' to generate an AI-generated content estimate and SEO grade.",
    "The tool will display the AI percentage and SEO grading based on the analysis.",
  ];

  return (
    <Row style={{ padding: "10px" }}>
      <Col>
        <HelpModal
          helpSteps={helpSteps}
          show={showVideoModal}
          close={closeVideoModal}
          videoUrl="https://www.youtube.com/embed/hNce9hFpBOY"
        />
        {!isInIframe && (
          <ToolsHeader
            title="SEO Graded Content Analyzer"
            description="Analyze your content to determine if it was generated by AI and assess
          its SEO optimization quality."
          />
        )}
        {/* <h1 className="text-center mb-3">SEO Graded Content Analyzer</h1>
        <p className="text-center mb-4">
          Analyze your content to determine if it was generated by AI and assess
          its SEO optimization quality.
        </p> */}
        {/* <div className="d-flex justify-content-center mb-4">
          <Button
            variant="outline-primary d-flex justify-content-center align-items-center"
            size="sm"
            onClick={openVideoModal}
          >
            See how to use <IoMdPlay className="ms-2" />
          </Button>
        </div> */}

        <Card
          className="p-4 w-100"
          style={{
            maxWidth: "100%",
            boxShadow:
              "rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px",
          }}
        >
          <Form.Group controlId="contentInput" className="mb-4">
            <Form.Label>Content to Analyze</Form.Label>
            <Form.Control
              as="textarea"
              rows={6}
              placeholder="Paste the content you want to analyze for AI and SEO"
              value={content}
              onChange={handleContentChange}
              style={{ resize: "vertical" }}
              isInvalid={!!minLengthError}
            />
            <Form.Control.Feedback type="invalid">
              {minLengthError}
            </Form.Control.Feedback>
          </Form.Group>

          <div className="text-center">
            {isLoading && (
              <Spinner animation="border" role="status" className="spinner">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            )}
            {!isLoading && (
              <Button variant="primary" onClick={analyzeContent}>
                Analyze Content
              </Button>
              // <Button
              //   variant="primary rounded-pill"
              //   size="lg"
              //   onClick={analyzeContent}
              // >
              //   Analyze Content
              // </Button>
            )}
          </div>

          <div className="mt-4">
            <Row>
              <Col md={6}>
                {seoGrade && optimizedContent && (
                  <Card
                    className="p-3 shadow-sm mt-4 text-center"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexDirection: "row",
                      gap: "10px",
                      height: "150px",
                    }}
                  >
                    <div
                      style={{
                        width: "120px",
                        height: "120px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {renderDonutChart(parseInt(optimizedContent))}
                    </div>

                    <div
                      style={{
                        textAlign: "left",
                        flex: 1,
                        marginLeft: "10px",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                      }}
                    >
                      <h4 style={{ margin: 0 }}>SEO Grading</h4>
                      <p style={{ margin: 0 }}>{`SEO Grade: ${seoGrade}`}</p>
                    </div>
                  </Card>
                )}
              </Col>

              <Col md={6}>
                {aiPercentage !== null && (
                  <Card
                    className="shadow-sm"
                    style={{
                      marginTop: "24px",
                      padding: "2.2rem !important",
                      height: "150px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "left",
                    }}
                  >
                    <div
                      style={{
                        textAlign: "left",
                        flex: 1,
                        marginLeft: "40px",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                      }}
                    >
                      <h4 style={{ margin: 0 }}>
                        AI-Generated Content Estimate
                      </h4>
                      <p>{`This content is approximately ${aiPercentage} likely to be AI-generated.`}</p>
                    </div>
                  </Card>
                )}
              </Col>
            </Row>
          </div>
        </Card>
      </Col>
    </Row>
  );
};
